import { Link } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import PageHeader from "@/components/feature/PageHeader";
import Tag from "@/components/base/Tag";
import { awards, profileInfo, timeline } from "@/mocks/site";
import { newsList } from "@/mocks/news";

export default function ProfilePage() {
  useSeo({
    title: "佐佐木明希 - 演艺资料与个人简介",
    description:
      "佐佐木明希的综合个人资料与职业经历介绍，包含基本资料、演艺经历、主要获奖情况与最新动态，全面了解这位演员的成长轨迹。",
    keywords: "佐佐木明希资料,个人简介,演艺经历,获奖,明星档案",
  });

  const latestNews = [...newsList].sort((a, b) => b.date.localeCompare(a.date)).slice(0, 4);

  return (
    <>
      <PageHeader
        crumbs={[{ label: "首页", path: "/" }, { label: "明星资料" }]}
        title="佐佐木明希 · 演艺资料"
        lead="从舞台剧到银幕，佐佐木明希用十年时间形成了克制而细腻的表演风格。这里汇总了她的基本资料、演艺经历与主要获奖情况。"
      />

      <section className="bg-background-50 py-14 md:py-16">
        <div className="mx-auto w-full max-w-[1200px] px-4 md:px-6">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
            <div className="lg:col-span-4">
              <div className="overflow-hidden rounded-lg border border-background-200">
                <div className="relative w-full pt-[120%]">
                  <img
                    src={profileInfo.avatar}
                    alt="佐佐木明希个人写真 - 明星资料页"
                    title="佐佐木明希 个人资料 写真"
                    className="absolute inset-0 h-full w-full object-cover object-top"
                  />
                </div>
              </div>
              <div className="mt-5 rounded-lg border border-background-200 bg-background-100 p-5">
                <p className="font-heading text-lg font-bold text-foreground-950">{profileInfo.name}</p>
                <p className="mt-1 text-sm text-foreground-500">
                  {profileInfo.nameAlt} · {profileInfo.roman}
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {profileInfo.specialties.map((item) => (
                    <Tag key={item} tone="secondary">
                      {item}
                    </Tag>
                  ))}
                </div>
              </div>
            </div>

            <div className="lg:col-span-8">
              <div className="rounded-lg border border-accent-200 bg-accent-50 p-6">
                <i className="ri-double-quotes-l text-2xl text-accent-500" aria-hidden="true" />
                <p className="mt-2 font-heading text-base leading-relaxed text-foreground-800 md:text-lg">
                  {profileInfo.quote}
                </p>
              </div>

              <h2 className="mt-9 font-heading text-xl font-bold text-foreground-950 md:text-2xl">基本资料</h2>
              <dl className="mt-5 grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2">
                <div className="border-b border-background-200 pb-3">
                  <dt className="text-xs font-label uppercase tracking-wider text-foreground-500">出生日期</dt>
                  <dd className="mt-1 text-sm text-foreground-900">{profileInfo.birthday}</dd>
                </div>
                <div className="border-b border-background-200 pb-3">
                  <dt className="text-xs font-label uppercase tracking-wider text-foreground-500">国籍 / 出生地</dt>
                  <dd className="mt-1 text-sm text-foreground-900">{profileInfo.birthplace}</dd>
                </div>
                <div className="border-b border-background-200 pb-3">
                  <dt className="text-xs font-label uppercase tracking-wider text-foreground-500">身高</dt>
                  <dd className="mt-1 text-sm text-foreground-900">{profileInfo.height}</dd>
                </div>
                <div className="border-b border-background-200 pb-3">
                  <dt className="text-xs font-label uppercase tracking-wider text-foreground-500">出道时间</dt>
                  <dd className="mt-1 text-sm text-foreground-900">{profileInfo.debut}</dd>
                </div>
                <div className="border-b border-background-200 pb-3 sm:col-span-2">
                  <dt className="text-xs font-label uppercase tracking-wider text-foreground-500">所属事务所</dt>
                  <dd className="mt-1 text-sm text-foreground-900">{profileInfo.agency}</dd>
                </div>
                <div className="sm:col-span-2">
                  <dt className="text-xs font-label uppercase tracking-wider text-foreground-500">代表作品</dt>
                  <dd className="mt-2 flex flex-wrap gap-2">
                    {profileInfo.representativeWorks.map((item) => (
                      <Tag key={item} tone="primary">
                        {item}
                      </Tag>
                    ))}
                  </dd>
                </div>
              </dl>

              <h2 className="mt-10 font-heading text-xl font-bold text-foreground-950 md:text-2xl">演艺经历简介</h2>
              <div className="mt-4 space-y-4">
                {profileInfo.bio.map((paragraph, index) => (
                  <p key={index} className="text-sm leading-loose text-foreground-700 md:text-base">
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background-100 py-14 md:py-16">
        <div className="mx-auto w-full max-w-[1200px] px-4 md:px-6">
          <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">演艺时间线</h2>
          <ol className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {timeline.map((item) => (
              <li key={item.year} className="rounded-lg border border-background-200 bg-background-50 p-6">
                <span className="inline-flex items-center gap-2 font-heading text-2xl font-bold text-primary-600">
                  <span className="h-2 w-2 rounded-full bg-accent-500" />
                  {item.year}
                </span>
                <p className="mt-3 text-sm leading-relaxed text-foreground-700">{item.text}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="bg-background-50 py-14 md:py-16">
        <div className="mx-auto w-full max-w-[1200px] px-4 md:px-6">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
            <div className="lg:col-span-7">
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">主要获奖情况</h2>
              <p className="mt-2 text-xs text-foreground-500">以下获奖与提名均为演示示例数据</p>
              <ul className="mt-6 divide-y divide-background-200">
                {awards.map((award, index) => (
                  <li key={index} className="flex flex-col gap-2 py-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground-900">{award.name}</p>
                      <p className="mt-1 text-xs text-foreground-500">
                        {award.year} · 作品《{award.work}》
                      </p>
                    </div>
                    <Tag tone={award.result === "获奖" ? "accent" : "secondary"}>{award.result}</Tag>
                  </li>
                ))}
              </ul>
            </div>

            <div className="lg:col-span-5">
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">最新动态</h2>
              <ul className="mt-6 space-y-4">
                {latestNews.map((news) => (
                  <li key={news.id}>
                    <Link
                      to="/news"
                      className="group block rounded-lg border border-background-200 bg-background-100 p-4 transition-colors hover:border-primary-300"
                    >
                      <span className="text-xs text-foreground-500">{news.date}</span>
                      <p className="mt-1.5 text-sm font-medium leading-snug text-foreground-900 transition-colors group-hover:text-primary-700">
                        {news.title}
                      </p>
                    </Link>
                  </li>
                ))}
              </ul>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/news"
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  查看全部资讯
                  <i className="ri-arrow-right-line" aria-hidden="true" />
                </Link>
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-label font-medium text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-700"
                >
                  返回首页
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}